/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Geometry for the geofence screens: between Fresh's points and a drawn shape, and the size,
 * centre and bounds the detail sheet and editor show. Pure, so it is unit-tested on its own.
 */

import type { GeofenceBounds, GeofencePoint, GeofenceShape, ShapeType } from './types'

/** WGS84 equatorial radius, as Mapbox and Turf use for areas. */
const EARTH_RADIUS_M = 6378137

type Position = [number, number]

const toRadians = (degrees: number) => (degrees * Math.PI) / 180

const samePosition = (a: Position, b: Position) => a[0] === b[0] && a[1] === b[1]

/** Close a ring of [longitude, latitude] positions: the first position is repeated at the end. */
export const closeRing = (positions: Position[]): Position[] => {
  if (positions.length === 0) return []
  return samePosition(positions[0], positions[positions.length - 1]) ? positions : [...positions, positions[0]]
}

/** The ring's distinct vertices: the closing repeat dropped. */
export const ringVertices = (ring: Position[]): Position[] =>
  ring.length > 1 && samePosition(ring[0], ring[ring.length - 1]) ? ring.slice(0, -1) : ring

/** The list endpoint's [latitude, longitude] outline as a closed GeoJSON ring. */
export const ringFromCoordinates = (coordinates: [number, number][]): Position[] =>
  closeRing(coordinates.map(([latitude, longitude]) => [longitude, latitude]))

/** Fresh's points, in point order, as a closed GeoJSON ring. */
export const ringFromPoints = (points: GeofencePoint[]): Position[] =>
  closeRing([...points].sort((a, b) => a.pointIndex - b.pointIndex).map((p) => [p.longitude, p.latitude]))

export const shapeFromPoints = (points: GeofencePoint[], type: ShapeType): GeofenceShape | null => {
  const ring = ringFromPoints(points)
  return ringVertices(ring).length >= 3 ? { type, ring } : null
}

/**
 * A shape as Fresh points, numbered from 1. The ring stays closed (the first point repeated at
 * the end), which is how Refresh saves them: Bing hands back closed rings, and its circles
 * repeat the 0°/360° point.
 */
export const pointsFromShape = (shape: GeofenceShape): GeofencePoint[] =>
  closeRing(shape.ring).map(([longitude, latitude], index) => ({ latitude, longitude, pointIndex: index + 1 }))

export const boundsOf = (ring: Position[]): GeofenceBounds | null => {
  if (ring.length === 0) return null
  const longitudes = ring.map((p) => p[0])
  const latitudes = ring.map((p) => p[1])
  return {
    north: Math.max(...latitudes),
    south: Math.min(...latitudes),
    east: Math.max(...longitudes),
    west: Math.min(...longitudes),
  }
}

/** The mean of the vertices: the middle of a circle or rectangle, near enough for a polygon. */
export const centreOf = (ring: Position[]): { latitude: number; longitude: number } | null => {
  const vertices = ringVertices(ring)
  if (vertices.length === 0) return null
  const sum = vertices.reduce((acc, [lng, lat]) => [acc[0] + lng, acc[1] + lat], [0, 0])
  return { longitude: sum[0] / vertices.length, latitude: sum[1] / vertices.length }
}

/** Great-circle distance in metres. */
export const distanceMeters = (a: Position, b: Position): number => {
  const dLat = toRadians(b[1] - a[1])
  const dLng = toRadians(b[0] - a[0])
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(a[1])) * Math.cos(toRadians(b[1])) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** The area enclosed on the sphere, in square metres (the formula Turf's `area` uses). */
export const areaSquareMeters = (ring: Position[]): number => {
  const vertices = ringVertices(ring)
  const n = vertices.length
  if (n < 3) return 0
  let total = 0
  for (let i = 0; i < n; i++) {
    const previous = vertices[(i - 1 + n) % n]
    const next = vertices[(i + 1) % n]
    total += (toRadians(next[0]) - toRadians(previous[0])) * Math.sin(toRadians(vertices[i][1]))
  }
  return Math.abs((total * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2)
}

export const perimeterMeters = (ring: Position[]): number => {
  const closed = closeRing(ring)
  let total = 0
  for (let i = 1; i < closed.length; i++) total += distanceMeters(closed[i - 1], closed[i])
  return total
}

/** A circle's radius: the mean distance from its centre to its edge. */
export const radiusMeters = (ring: Position[]): number => {
  const centre = centreOf(ring)
  const vertices = ringVertices(ring)
  if (!centre || vertices.length === 0) return 0
  const middle: Position = [centre.longitude, centre.latitude]
  return vertices.reduce((sum, vertex) => sum + distanceMeters(middle, vertex), 0) / vertices.length
}

const orientation = (a: Position, b: Position, c: Position) => {
  const value = (b[1] - a[1]) * (c[0] - b[0]) - (b[0] - a[0]) * (c[1] - b[1])
  return value === 0 ? 0 : value > 0 ? 1 : -1
}

const segmentsCross = (a: Position, b: Position, c: Position, d: Position) =>
  orientation(a, b, c) !== orientation(a, b, d) && orientation(c, d, a) !== orientation(c, d, b)

/** Whether any two non-adjacent edges cross: a bow-tie, which alerts can't reason about. */
export const isSelfIntersecting = (ring: Position[]): boolean => {
  const vertices = ringVertices(ring)
  const n = vertices.length
  if (n < 4) return false
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      // Edges that share a vertex always "touch"; skip them.
      if (j === i + 1 || (i === 0 && j === n - 1)) continue
      if (segmentsCross(vertices[i], vertices[(i + 1) % n], vertices[j], vertices[(j + 1) % n])) return true
    }
  }
  return false
}

export const formatArea = (squareMeters: number): string => {
  if (squareMeters >= 1_000_000)
    return `${(squareMeters / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })} km²`
  if (squareMeters >= 10_000)
    return `${(squareMeters / 10_000).toLocaleString(undefined, { maximumFractionDigits: 1 })} ha`
  return `${Math.round(squareMeters).toLocaleString()} m²`
}

export const formatLength = (meters: number): string =>
  meters >= 1000
    ? `${(meters / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} km`
    : `${Math.round(meters).toLocaleString()} m`
